import { LinkProps } from "@tanstack/react-router"
import { TFunction } from "i18next"
import { usePredefinedSegmentColors } from "modules/segments/hooks/usePredefinedSegmentColors"
import { usePredefinedSegmentTranslations } from "modules/segments/hooks/usePredefinedSegmentTranslations"
import { PresenceScore } from "modules/shared/components/Scores/PresenceScore"
import { SentimentScore } from "modules/shared/components/Scores/SentimentScore"
import { SolveRateScore } from "modules/shared/components/Scores/SolveRateScore"
import { UrgencyScore } from "modules/shared/components/Scores/UrgencyScore"
import { getFulfillmentColor, getQuestionSolveRateColor } from "modules/shared/lib/sentiment"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import {
  CanonicalTopicGroupedBy,
  CanonicalTopicGroupedByEnum,
  ClusterDomain,
  ClusterDomainEnum,
  SegmentGroupResponseDataSchema,
} from "schema"
import z from "zod"
import { ColorByEnum } from "../lib/colorBy"

type Node = {
  avgSentiment: number | null
  rings?: 1 | 2 | 3 | null
  avgSolveRate?: number | null
  avgPotentialSolveRate?: number | null
}

type NavigateToParams = {
  node: {
    id?: number
    snapshotIds?: number[]
    segment?: string
  }
  domain: ClusterDomain
  canonicalId: string
  campaignId?: string
  segmentId?: string
  goalId?: string | null
}

function getPotentialRate(rate: number | null | undefined, gap: number | null | undefined) {
  if (rate == null || gap == null) return null

  return Math.min(1, rate + gap)
}

type DomainConfig = {
  domain: ClusterDomain
  goalId?: string | null
  colorBy: keyof typeof ColorByEnum
  scoreLabel: string
  noDataTitle: string
  noDataDescription: string
  getGroupColor: (groupName: string, segmentGroupName: string | undefined | null) => string
  getGroupLabel: (
    groupName: string,
    segmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>,
  ) => string
  getScoreComponent: (node: Node) => React.ReactNode
  getRingsFromNode?: (item: { totalItems: number }) => 1 | 2 | 3
  getMaxItems?: (groupedBy: CanonicalTopicGroupedBy, dataLength?: number) => number
  getNavigateTo?: (params: NavigateToParams) => LinkProps | undefined
}

function getConfig(
  domain: ClusterDomain,
  goalId: string | null,
  tagBySentiment: boolean,
  t: TFunction,
  getSegmentTranslation: (
    segmentName: string,
    segmentGroup?: z.infer<typeof SegmentGroupResponseDataSchema>,
  ) => string,
  predefinedSegmentColors: Record<string, Record<string, string>>,
): DomainConfig {
  switch (domain) {
    case ClusterDomainEnum.VoiceOfCustomer:
    case ClusterDomainEnum.Goal:
      return {
        domain: goalId ? ClusterDomainEnum.Goal : ClusterDomainEnum.VoiceOfCustomer,
        goalId,
        colorBy: tagBySentiment ? ColorByEnum.Sentiment : ColorByEnum.Presence,
        scoreLabel: tagBySentiment ? t("shared.rates.sentiment") : t("shared.rates.presence"),
        noDataTitle: goalId
          ? t("dashboard.noDataToDisplay")
          : t("dashboard.voiceOfCustomer.noData.title"),
        noDataDescription: t("dashboard.voiceOfCustomer.noData.description"),
        getGroupColor: (groupName, segmentGroupName) => {
          const color =
            (segmentGroupName && predefinedSegmentColors[segmentGroupName]?.[groupName]) ||
            "neutral"
          return `var(--${color})`
        },
        getGroupLabel: (groupName, segmentGroup) => {
          return getSegmentTranslation(groupName, segmentGroup)
        },
        getScoreComponent: (node) => {
          if (tagBySentiment) return <SentimentScore sentiment={node.avgSentiment} />
          return <PresenceScore rings={node.rings} />
        },
        getMaxItems: (groupedBy) => {
          switch (groupedBy.type) {
            case CanonicalTopicGroupedByEnum.company:
              return 1
            default:
              return 3
          }
        },
        getNavigateTo: ({ node, segmentId, canonicalId, campaignId, goalId }) => {
          if (!node.id) return undefined

          return {
            to: "/conversations/list",
            search: {
              filterBy: {
                domain: goalId ? ClusterDomainEnum.Goal : ClusterDomainEnum.VoiceOfCustomer,
                canonicalId,
                segmentId,
                campaignId,
                goalId,
              },
            },
          }
        },
      }

    case ClusterDomainEnum.Support:
      return {
        domain: ClusterDomainEnum.Support,
        colorBy: ColorByEnum.SolveRate,
        scoreLabel: t("shared.rates.solveRate"),
        noDataTitle: t("dashboard.support.noData.title"),
        noDataDescription: t("dashboard.support.noData.description"),
        getGroupColor: (groupName, segmentGroupName) => {
          const color =
            (segmentGroupName && predefinedSegmentColors[segmentGroupName]?.[groupName]) ||
            "neutral"
          return `var(--${color})`
        },
        getGroupLabel: (groupName, segmentGroup) => {
          return getSegmentTranslation(groupName, segmentGroup)
        },
        getScoreComponent: (node) => (
          <SolveRateScore
            solveRate={node.avgSolveRate}
            potentialSolveRate={node.avgPotentialSolveRate}
          />
        ),
        getMaxItems: (groupedBy, dataLength) => {
          switch (groupedBy.type) {
            case CanonicalTopicGroupedByEnum.company:
              return 1
            default:
              return Math.min(dataLength ?? 3, 4) // Cap at 4 for better layout
          }
        },
        getNavigateTo: ({ node, segmentId, canonicalId, campaignId }) => {
          if (!node.id) return undefined

          return {
            to: "/support/list",
            search: {
              supportTab: "questions",
              filterBy: {
                canonicalId,
                segmentId,
                campaignId,
              },
            },
          }
        },
      }

    case ClusterDomainEnum.Question:
      return {
        domain: ClusterDomainEnum.Question,
        colorBy: ColorByEnum.QuestionSolveRate,
        scoreLabel: t("shared.rates.answeredRate"),
        noDataTitle: t("dashboard.question.noData.title"),
        noDataDescription: t("dashboard.question.noData.description"),
        getGroupColor: (groupName, segmentGroupName) => {
          const color =
            (segmentGroupName && predefinedSegmentColors[segmentGroupName]?.[groupName]) ||
            "neutral"
          return `var(--${color})`
        },
        getGroupLabel: (groupName, segmentGroup) => {
          return getSegmentTranslation(groupName, segmentGroup)
        },
        getScoreComponent: (node) => (
          <SolveRateScore
            solveRate={node.avgSolveRate}
            labelKey="shared.rates.answeredRate"
            getColor={getQuestionSolveRateColor}
          />
        ),
        getMaxItems: (groupedBy, dataLength) => {
          switch (groupedBy.type) {
            case CanonicalTopicGroupedByEnum.company:
              return 1
            default:
              return Math.min(dataLength ?? 3, 4)
          }
        },
        getNavigateTo: ({ node, segmentId, canonicalId, campaignId }) =>
          getSupportNavigateTo({
            node,
            supportTab: "newQuestions",
            segmentId,
            canonicalId,
            campaignId,
          }),
      }

    case ClusterDomainEnum.Issue:
      return {
        domain: ClusterDomainEnum.Issue,
        colorBy: ColorByEnum.Urgency,
        scoreLabel: t("shared.rates.urgency"),
        noDataTitle: t("dashboard.issue.noData.title"),
        noDataDescription: t("dashboard.issue.noData.description"),
        getGroupColor: (groupName, segmentGroupName) => {
          const color =
            (segmentGroupName && predefinedSegmentColors[segmentGroupName]?.[groupName]) ||
            "neutral"
          return `var(--${color})`
        },
        getGroupLabel: (groupName, segmentGroup) => {
          return getSegmentTranslation(groupName, segmentGroup)
        },
        getScoreComponent: (node) => (
          <UrgencyScore
            highRate={node.avgSolveRate}
            highAndMediumRate={node.avgPotentialSolveRate}
          />
        ),
        getMaxItems: (groupedBy, dataLength) => {
          switch (groupedBy.type) {
            case CanonicalTopicGroupedByEnum.company:
              return 1
            default:
              return Math.min(dataLength ?? 3, 4)
          }
        },
        getNavigateTo: ({ node, segmentId, canonicalId, campaignId }) =>
          getSupportNavigateTo({ node, supportTab: "issues", segmentId, canonicalId, campaignId }),
      }

    case ClusterDomainEnum.ActionRequest:
      return {
        domain: ClusterDomainEnum.ActionRequest,
        colorBy: ColorByEnum.Fulfillment,
        scoreLabel: t("shared.rates.fulfillmentRate"),
        noDataTitle: t("dashboard.actionRequest.noData.title"),
        noDataDescription: t("dashboard.actionRequest.noData.description"),
        getGroupColor: (groupName, segmentGroupName) => {
          const color =
            (segmentGroupName && predefinedSegmentColors[segmentGroupName]?.[groupName]) ||
            "neutral"
          return `var(--${color})`
        },
        getGroupLabel: (groupName, segmentGroup) => {
          return getSegmentTranslation(groupName, segmentGroup)
        },
        getScoreComponent: (node) => (
          <SolveRateScore
            solveRate={node.avgSolveRate}
            potentialSolveRate={getPotentialRate(node.avgSolveRate, node.avgPotentialSolveRate)}
            labelKey="shared.rates.fulfillmentRate"
            getColor={getFulfillmentColor}
            potentialColor="var(--chart-2)"
          />
        ),
        getMaxItems: (groupedBy, dataLength) => {
          switch (groupedBy.type) {
            case CanonicalTopicGroupedByEnum.company:
              return 1
            default:
              return Math.min(dataLength ?? 3, 4)
          }
        },
        getNavigateTo: ({ node, segmentId, canonicalId, campaignId }) =>
          getSupportNavigateTo({
            node,
            supportTab: "actionRequests",
            segmentId,
            canonicalId,
            campaignId,
          }),
      }

    default:
      throw new Error(`Unsupported domain: ${domain}`)
  }
}

// NOTE: It's important that is is a hook, so hooks using it do not re-render when they should not
export function useTreemapConfig(
  domain: ClusterDomain,
  goalId: string | null,
  tagBySentiment: boolean,
): DomainConfig {
  const { t } = useTranslation()

  const { getSegmentTranslation } = usePredefinedSegmentTranslations()
  const predefinedSegmentColors = usePredefinedSegmentColors()

  return useMemo(
    () =>
      getConfig(domain, goalId, tagBySentiment, t, getSegmentTranslation, predefinedSegmentColors),
    [domain, goalId, tagBySentiment, t, getSegmentTranslation, predefinedSegmentColors],
  )
}

function getSupportNavigateTo({
  node,
  supportTab,
  segmentId,
  canonicalId,
  campaignId,
}: Pick<NavigateToParams, "node" | "segmentId" | "canonicalId" | "campaignId"> & {
  supportTab: "newQuestions" | "issues" | "actionRequests"
}) {
  if (!node.id) return undefined

  return {
    to: "/support/list",
    search: {
      supportTab,
      filterBy: {
        canonicalId,
        segmentId,
        campaignId,
      },
    },
  } satisfies LinkProps
}
